<template>
  <section id="register" class="section-padding relative overflow-hidden transition-colors duration-500"
    :class="isDark ? 'galaxy-section-alt-dark' : 'galaxy-section-alt-light'"
  >
    <!-- Background effects -->
    <div class="absolute inset-0 pointer-events-none overflow-hidden">
      <div class="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-10"
        :class="isDark ? 'bg-nebula-purple' : 'bg-primary-200'"
      ></div>
      <div class="absolute bottom-0 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-10"
        :class="isDark ? 'bg-nebula-cyan' : 'bg-accent-200'"
      ></div>
      <div class="absolute top-1/2 left-0 w-64 h-64 rounded-full blur-3xl opacity-5"
        :class="isDark ? 'bg-nebula-pink' : 'bg-secondary-200'"
      ></div>
    </div>

    <div class="max-w-3xl mx-auto relative z-10">
      <!-- Section header -->
      <div class="text-center mb-10">
        <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border"
          :class="isDark
            ? 'bg-nebula-purple/10 border-nebula-purple/30 text-nebula-cyan'
            : 'bg-primary-50 border-primary-200 text-primary-700'"
        >
          <span class="material-icons text-base">star</span>
          {{ t('registration.badge') }}
        </div>
        <h2 class="text-3xl md:text-4xl font-bold mb-4">
          <span class="galaxy-text-gradient">{{ t('registration.sectionTitle') }}</span>
        </h2>
        <p class="text-lg max-w-xl mx-auto leading-relaxed"
          :class="isDark ? 'text-galaxy-200' : 'text-gray-600'"
        >
          {{ t('registration.subtitle') }}
        </p>
      </div>

      <!-- Gradient border wrapper -->
      <div class="p-[2px] rounded-2xl bg-nebula-gradient shadow-glow-lg">
        <div class="rounded-2xl overflow-hidden"
          :class="isDark ? 'bg-space-800/95' : 'bg-white/98'"
        >
          <!-- Top header bar -->
          <div class="px-6 py-4 flex items-center gap-3 border-b"
            :class="isDark ? 'border-galaxy-700/30 bg-space-900/60' : 'border-gray-100 bg-primary-50/60'"
          >
            <!-- Traffic-light dots -->
            <div class="flex gap-1.5">
              <span class="h-3 w-3 rounded-full bg-nebula-pink/80"></span>
              <span class="h-3 w-3 rounded-full bg-star-gold/80"></span>
              <span class="h-3 w-3 rounded-full bg-nebula-cyan/80"></span>
            </div>
            <div class="flex-1 flex justify-center">
              <div class="flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full"
                :class="isDark ? 'bg-galaxy-800/60 text-galaxy-200' : 'bg-white text-gray-500 border border-gray-200'"
              >
                <span class="material-icons text-xs"
                  :class="isDark ? 'text-nebula-cyan' : 'text-primary-500'"
                >lock</span>
                forms.gle · {{ t('registration.secureForm') }}
              </div>
            </div>
          </div>

          <!-- Trust indicators -->
          <div class="flex flex-wrap justify-center gap-x-6 gap-y-2 px-6 py-4 border-b"
            :class="isDark ? 'border-galaxy-700/20 bg-space-800/40' : 'border-gray-100 bg-gray-50/50'"
          >
            <div v-for="badge in trustBadges" :key="badge.icon"
              class="flex items-center gap-1.5 text-xs font-medium"
              :class="isDark ? 'text-galaxy-300' : 'text-gray-500'"
            >
              <span class="material-icons text-sm"
                :class="isDark ? 'text-nebula-cyan' : 'text-primary-500'"
              >{{ badge.icon }}</span>
              {{ badge.label }}
            </div>
          </div>

          <!-- iframe loading skeleton -->
          <div v-if="iframeLoading" class="space-y-3 p-6 animate-pulse">
            <div class="h-8 rounded-lg w-1/2 mx-auto mb-6"
              :class="isDark ? 'bg-galaxy-700/50' : 'bg-gray-200'"
            ></div>
            <div v-for="n in 6" :key="n" class="h-10 rounded-lg"
              :class="[isDark ? 'bg-galaxy-700/40' : 'bg-gray-100', n % 3 === 0 ? 'w-3/4' : 'w-full']"
            ></div>
            <div class="h-20 rounded-lg w-full mt-2"
              :class="isDark ? 'bg-galaxy-700/40' : 'bg-gray-100'"
            ></div>
            <div class="h-10 rounded-full w-40 mx-auto mt-4"
              :class="isDark ? 'bg-galaxy-600/50' : 'bg-primary-200'"
            ></div>
          </div>

          <!-- Google Form iframe -->
          <div :class="iframeLoading ? 'h-0 overflow-hidden' : ''">
            <iframe
              src="https://docs.google.com/forms/d/e/1FAIpQLSciG2di4l2bSbJZi0c2a0JZF7tPHaEOKJY0zRtgz5nB1D71YQ/viewform?embedded=true"
              :title="t('registration.formTitle')"
              width="100%"
              height="3359"
              frameborder="0"
              marginheight="0"
              marginwidth="0"
              loading="lazy"
              class="block w-full"
              style="min-height: 400px;"
              @load="iframeLoading = false"
            >
              {{ t('registration.iframeLoading') }}
            </iframe>
          </div>

          <!-- Bottom bar -->
          <div class="px-6 py-3 flex items-center justify-center gap-2 border-t"
            :class="isDark ? 'border-galaxy-700/30 bg-space-900/60' : 'border-gray-100 bg-gray-50/60'"
          >
            <span class="material-icons text-sm"
              :class="isDark ? 'text-nebula-cyan' : 'text-primary-500'"
            >verified_user</span>
            <p class="text-xs"
              :class="isDark ? 'text-galaxy-400' : 'text-gray-400'"
            >
              {{ t('registration.privacyNote') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{ isDark: boolean }>()

const { t } = useI18n()
const iframeLoading = ref(true)

const trustBadges = computed(() => [
  { icon: 'verified', label: t('registration.trust.certified') },
  { icon: 'schedule', label: t('registration.trust.response') },
])
</script>
